import type {
  ApplovinClickIds,
  ApplovinLanding,
  MarketingTracking,
} from '@openeducation/pp-rn-shared-types';

const AXWRT_COOKIE_NAME = '_axwrt';
const LANDING_STORAGE_KEY_PREFIX = 'applovin-landing:';

const getLocationSearch = () =>
  typeof window !== 'undefined' ? window.location?.search ?? '' : '';

const getDocumentCookie = () =>
  typeof document !== 'undefined' ? document.cookie ?? '' : '';

// AppLovin adds aleid and alart to the checkout URL.
export function getApplovinClickIds(
  search: string = getLocationSearch()
): ApplovinClickIds {
  const params = new URLSearchParams(search);
  const aleid = params.get('aleid');
  const alart = params.get('alart');
  return {
    ...(aleid ? { aleid } : {}),
    ...(alart ? { alart } : {}),
  };
}

// All the AppLovin tracking runs only when the URL has aleid or alart.
export function hasApplovinClickId({ aleid, alart }: ApplovinClickIds) {
  return !!aleid || !!alart;
}

// The Axon pixel sets the _axwrt cookie.
export function getAxwrtCookie(
  cookie: string = getDocumentCookie()
): string | undefined {
  const prefix = `${AXWRT_COOKIE_NAME}=`;
  const value = cookie
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(prefix))
    ?.slice(prefix.length);
  return value || undefined;
}

// The pixel can set the cookie after the checkout loads, so check it again
// until it is there or the time is over.
export function waitForAxwrtCookie({
  intervalMs = 100,
  timeoutMs = 1500,
}: { intervalMs?: number; timeoutMs?: number } = {}): Promise<
  string | undefined
> {
  return new Promise((resolve) => {
    let elapsedMs = 0;
    const check = () => {
      const axwrt = getAxwrtCookie();
      if (axwrt || elapsedMs >= timeoutMs) {
        resolve(axwrt);
        return;
      }
      elapsedMs += intervalMs;
      setTimeout(check, intervalMs);
    };
    check();
  });
}

const getLandingKey = (sfdcToken: string) =>
  `${LANDING_STORAGE_KEY_PREFIX}${sfdcToken}`;

const getUrlLanding = (): ApplovinLanding | undefined => {
  const clickIds = getApplovinClickIds();
  return hasApplovinClickId(clickIds)
    ? { ...clickIds, eventSourceUrl: window.location.href }
    : undefined;
};

const readSavedLanding = (sfdcToken: string): ApplovinLanding | undefined => {
  try {
    const saved = sessionStorage.getItem(getLandingKey(sfdcToken));
    const landing = saved ? JSON.parse(saved) : undefined;
    return landing &&
      hasApplovinClickId(landing) &&
      typeof landing.eventSourceUrl === 'string'
      ? landing
      : undefined;
  } catch {
    return undefined;
  }
};

// The PayPal flow leaves the checkout and comes back to a URL without aleid
// and alart. Call this when the checkout loads, to keep the landing data in
// sessionStorage for the sfdcToken.
export function saveApplovinLanding(sfdcToken: string) {
  const landing = getUrlLanding();
  if (!landing) {
    return;
  }
  try {
    sessionStorage.setItem(getLandingKey(sfdcToken), JSON.stringify(landing));
  } catch {
    // No sessionStorage (native app, private mode): use the URL only.
  }
}

// Returns the landing data of the URL, or else the data saved for the
// sfdcToken. Returns undefined when the checkout does not come from an
// AppLovin click (no aleid and no alart).
export function getApplovinLanding(
  sfdcToken?: string
): ApplovinLanding | undefined {
  return (
    getUrlLanding() ?? (sfdcToken ? readSavedLanding(sfdcToken) : undefined)
  );
}

// Returns undefined when the checkout does not come from an AppLovin click.
export function getMarketingTracking(
  sfdcToken?: string
): MarketingTracking | undefined {
  const landing = getApplovinLanding(sfdcToken);
  if (!landing) {
    return undefined;
  }
  const axwrt = getAxwrtCookie();
  return {
    ...landing,
    ...(axwrt ? { axwrt } : {}),
  };
}

const getRandomBytes = (length: number): Uint8Array => {
  const { crypto } = globalThis;
  if (typeof crypto?.getRandomValues === 'function') {
    return crypto.getRandomValues(new Uint8Array(length));
  }
  // A dedupe_id must be unique, not secret.
  return Uint8Array.from({ length }, () => Math.floor(Math.random() * 256));
};

// Never throws: a tracking error must not stop the checkout.
export function createDedupeId(): string {
  const { crypto } = globalThis;
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // UUID v4 for browsers without crypto.randomUUID (Safari < 15.4).
  const bytes = getRandomBytes(16);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}
